import { ErreurApi, type Client } from '../api/client.js';
import type { QuestionBeta, Session } from '../api/contrat.js';
import type { PropsApp, EtatConnexion } from './App.js';
import {
  ETAT_BETA_INITIAL,
  contexteBeta,
  questionsDues,
  recoitBeta,
  reducerBeta,
  repondreBeta,
  type ActionBeta,
  type EtatBeta,
} from './beta.js';
import { revenirAMaVue } from './BandeauVoirEnTantQue.js';
import { soumettreConnexion, validerCode, type SaisieConnexion } from './connexion.js';
import { ouvrirLienMagique, type EtatLien } from './lien-magique.js';
import {
  ETAT_INSTALLATION_INITIAL,
  lancerInstallation,
  reducerInstallation,
  type EtatInstallation,
  type InvitationInstallation,
} from './pwa.js';
import { roleEffectif } from './roles.js';
import { resoudre } from './routeur.js';

/**
 * Contrôleur de la coquille : tient l’état (session, adresse, appareil, réseau, BETA, installation) et applique les
 * décisions du routeur. Il ne connaît ni le DOM ni React : l’environnement lui est injecté (adresse, historique,
 * largeur, réseau), ce qui permet de l’éprouver sans navigateur. `Racine` le branche sur `useSyncExternalStore`.
 */
export interface EnvControleur {
  client: Client;
  chemin: () => string;
  pousser: (chemin: string) => void;
  remplacer: (chemin: string) => void;
  largeur: () => number;
  enLigne: () => boolean;
}

export interface EtatApp {
  sessionChargee: boolean;
  session: Session | null;
  chemin: string;
  largeur: number;
  enLigne: boolean;
  enAttente: number;
  installation: EtatInstallation;
  beta: EtatBeta;
  saisieEnCours: boolean;
  appelEnCours: boolean;
  connexion: EtatConnexion;
  lien: EtatLien;
}

const CONNEXION_VIDE: EtatConnexion = { enCours: false, erreurs: {}, message: null };
const MAX_REDIRECTIONS = 4;

export class Controleur {
  private etat: EtatApp;
  private readonly abonnes = new Set<() => void>();
  private invitation: InvitationInstallation | null = null;
  private jetonOuvert: string | null = null;
  private ecranBeta: string | null = null;
  /** Adresse et mot de passe reconnus, gardés en mémoire le temps de la seconde étape (code à 6 chiffres). */
  private identifiantsRetenus: { email: string; motDePasse: string } | null = null;

  constructor(private readonly env: EnvControleur) {
    this.etat = {
      sessionChargee: false,
      session: null,
      chemin: env.chemin(),
      largeur: env.largeur(),
      enLigne: env.enLigne(),
      enAttente: 0,
      installation: ETAT_INSTALLATION_INITIAL,
      beta: ETAT_BETA_INITIAL,
      saisieEnCours: false,
      appelEnCours: false,
      connexion: CONNEXION_VIDE,
      lien: { etat: 'verification' },
    };
  }

  readonly subscribe = (ecoute: () => void): (() => void) => {
    this.abonnes.add(ecoute);
    return () => {
      this.abonnes.delete(ecoute);
    };
  };

  readonly getEtat = (): EtatApp => this.etat;

  private modifier(changements: Partial<EtatApp>): void {
    const suivant = { ...this.etat, ...changements };
    if ((Object.keys(changements) as (keyof EtatApp)[]).every((k) => Object.is(suivant[k], this.etat[k]))) return;
    this.etat = suivant;
    for (const ecoute of [...this.abonnes]) ecoute();
  }

  private dispatcherBeta(action: ActionBeta): void {
    this.modifier({ beta: reducerBeta(this.etat.beta, action) });
  }

  async demarrer(): Promise<void> {
    let session: Session | null = null;
    try {
      session = await this.env.client.appeler<Session>('auth.moi');
    } catch (e) {
      if (!(e instanceof ErreurApi)) throw e;
    }
    this.modifier({ sessionChargee: true, session });
    await this.appliquer();
  }

  /** Applique les décisions du routeur jusqu’à une page stable (redirections), puis déclenche ce que la page appelle. */
  private async appliquer(): Promise<void> {
    for (let i = 0; i < MAX_REDIRECTIONS; i++) {
      const decision = resoudre({ chemin: this.etat.chemin, session: this.etat.session, largeur: this.etat.largeur });
      if (decision.type === 'rediriger') {
        this.env.remplacer(decision.vers);
        this.modifier({ chemin: decision.vers });
        continue;
      }
      if (decision.type === 'lien-magique') await this.ouvrirLien(decision.jeton);
      else if (decision.type === 'ecran') await this.poserBeta(decision.ecran.id);
      return;
    }
  }

  private async ouvrirLien(jeton: string): Promise<void> {
    if (this.jetonOuvert === jeton) return;
    this.jetonOuvert = jeton;
    this.modifier({ lien: { etat: 'verification' } });
    const resultat = await ouvrirLienMagique(this.env.client, jeton);
    if (!resultat.ok) {
      this.modifier({ lien: { etat: 'erreur', message: resultat.message } });
      return;
    }
    this.env.remplacer('/');
    this.modifier({ session: resultat.session, chemin: '/' });
    await this.appliquer();
  }

  private async poserBeta(ecranId: string): Promise<void> {
    if (this.ecranBeta === ecranId) return;
    this.ecranBeta = ecranId;
    this.dispatcherBeta({ type: 'ecran-change' });
    const session = this.etat.session;
    if (!session || !recoitBeta(roleEffectif(session))) return;
    const questions = await questionsDues(this.env.client, contexteBeta(ecranId));
    if (this.ecranBeta === ecranId) this.dispatcherBeta({ type: 'recues', questions });
  }

  async naviguer(chemin: string): Promise<void> {
    this.env.pousser(chemin);
    this.modifier({ chemin });
    await this.appliquer();
  }

  async surChangementAdresse(): Promise<void> {
    this.modifier({ chemin: this.env.chemin() });
    await this.appliquer();
  }

  surRedimension(largeur: number): void {
    this.modifier({ largeur });
  }

  surReseau(enLigne: boolean): void {
    this.modifier({ enLigne });
  }

  definirEnAttente(enAttente: number): void {
    this.modifier({ enAttente });
  }

  definirSaisie(saisieEnCours: boolean): void {
    this.modifier({ saisieEnCours });
  }

  definirAppel(appelEnCours: boolean): void {
    this.modifier({ appelEnCours });
  }

  async seConnecter(saisie: SaisieConnexion): Promise<void> {
    const retenus = this.identifiantsRetenus;
    if (retenus) {
      const erreurCode = validerCode(saisie.totp);
      if (erreurCode) {
        this.modifier({ connexion: { enCours: false, erreurs: { totp: erreurCode }, message: null, etape: 'code' } });
        return;
      }
    }
    const complete = retenus ? { ...retenus, totp: saisie.totp } : { ...saisie, totp: '' };
    const etape = retenus ? ({ etape: 'code' } as const) : {};
    this.modifier({ connexion: { enCours: true, erreurs: {}, message: null, ...etape } });
    const resultat = await soumettreConnexion(this.env.client, complete);
    if (!resultat.ok) {
      if ('codeRequis' in resultat) {
        this.identifiantsRetenus = { email: complete.email.trim(), motDePasse: complete.motDePasse };
        this.modifier({ connexion: { enCours: false, erreurs: {}, message: null, etape: 'code' } });
        return;
      }
      this.modifier({
        connexion: { enCours: false, erreurs: resultat.erreurs ?? {}, message: resultat.erreurs ? null : resultat.message, ...etape },
      });
      return;
    }
    this.identifiantsRetenus = null;
    this.env.remplacer('/');
    this.modifier({ session: resultat.session, chemin: '/', connexion: CONNEXION_VIDE });
    await this.appliquer();
  }

  /** Seconde étape abandonnée : retour à l’adresse et au mot de passe, rien n’est gardé. */
  revenirAuxIdentifiants(): void {
    this.identifiantsRetenus = null;
    this.modifier({ connexion: CONNEXION_VIDE });
  }

  async seDeconnecter(): Promise<void> {
    try {
      await this.env.client.appeler('auth.deconnexion');
    } catch (e) {
      if (!(e instanceof ErreurApi)) throw e;
    }
    this.ecranBeta = null;
    this.jetonOuvert = null;
    this.env.remplacer('/connexion');
    this.modifier({
      session: null,
      chemin: '/connexion',
      beta: ETAT_BETA_INITIAL,
      connexion: CONNEXION_VIDE,
      lien: { etat: 'verification' },
    });
  }

  async revenirAMaVue(): Promise<void> {
    if (!this.etat.session) return;
    await revenirAMaVue(this.env.client, this.etat.session);
    this.ecranBeta = null;
    this.modifier({ session: await this.env.client.appeler<Session>('auth.moi') });
    await this.appliquer();
  }

  async repondreBeta(question: QuestionBeta, option: string): Promise<void> {
    try {
      await repondreBeta(this.env.client, question, option, this.ecranBeta ?? '');
      this.dispatcherBeta({ type: 'repondue', cle: question.key });
    } catch (e) {
      if (!(e instanceof ErreurApi)) throw e;
      // La réponse n’a pas pu partir : la question est remise à plus tard plutôt que de bloquer l’écran.
      this.dispatcherBeta({ type: 'plus-tard', cle: question.key });
    }
  }

  plusTardBeta(question: QuestionBeta): void {
    this.dispatcherBeta({ type: 'plus-tard', cle: question.key });
  }

  surInstallationProposee(invitation: InvitationInstallation): void {
    this.invitation = invitation;
    this.modifier({ installation: reducerInstallation(this.etat.installation, { type: 'proposee' }) });
  }

  surInstallee(): void {
    this.invitation = null;
    this.modifier({ installation: reducerInstallation(this.etat.installation, { type: 'installee' }) });
  }

  async installer(): Promise<void> {
    const invitation = this.invitation;
    this.invitation = null;
    this.modifier({ installation: await lancerInstallation(invitation) });
  }
}

export interface OptionsProprietes {
  /** Vrai quand l’application tourne sur les mocks : les pages portent l’étiquette « Données d’exemple ». */
  donneesExemple?: boolean;
}

/** Propriétés de `App` pour l’état courant du contrôleur : les rappels agissent sur le contrôleur. */
export function proprietesApp(ctrl: Controleur, options: OptionsProprietes = {}): PropsApp {
  const etat = ctrl.getEtat();
  return {
    chemin: etat.chemin,
    session: etat.session,
    largeur: etat.largeur,
    enLigne: etat.enLigne,
    enAttente: etat.enAttente,
    installation: etat.installation,
    questionsBeta: etat.beta.questions,
    reporteesBeta: etat.beta.reportees,
    saisieEnCours: etat.saisieEnCours,
    appelEnCours: etat.appelEnCours,
    connexion: etat.connexion,
    lien: etat.lien,
    donneesExemple: options.donneesExemple ?? false,
    onNaviguer: (chemin) => void ctrl.naviguer(chemin),
    onConnexion: (saisie) => void ctrl.seConnecter(saisie),
    onChangerAdresse: () => ctrl.revenirAuxIdentifiants(),
    onRevenir: () => void ctrl.revenirAMaVue(),
    onDeconnexion: () => void ctrl.seDeconnecter(),
    onInstaller: () => void ctrl.installer(),
    onRepondreBeta: (question, option) => void ctrl.repondreBeta(question, option),
    onPlusTardBeta: (question) => ctrl.plusTardBeta(question),
  };
}
